/*
 * Copyright (c) Castle Point Learning Systems Corporation, 2013-2023, all rights reserved.
 * NOTICE: Dissemination of the information and/or materials contained in this file is strictly
 * forbidden unless prior written permission is obtained from Castle Point Learning Systems Corporation.
 * See LICENSE.txt (part of this package) for the full notice.
 */
import { MathEvaluationMeaningType, MathParameterType, MathSelectionType, MathSourceActionParameterElementType, Status, } from '../types/model';
import { ADD_ROW, REWRITE_ROW } from './constants';
import { ActionParameterKey, ActionType } from '../types/rest-client';
import { MathNodeName } from 'semantic-math-editor';
export class SolutionConverter {
    constructor(solutionId, evaluation) {
        this.actions = [];
        this.expressionToParts = new Map();
        this.solutionId = solutionId;
        this.evaluation = evaluation;
        this.timestamp = Date.now();
    }
    static toMathSolution(solutionId, evaluation) {
        return new SolutionConverter(solutionId, evaluation).convert();
    }
    convert() {
        return {
            solution: {
                id: this.solutionId,
                name: 'tmp',
                author: {
                    id: 0,
                    permission: 'student',
                },
                completedAt: this.timestamp,
                updatedAt: this.timestamp,
                startedAt: this.timestamp,
                rows: this.convertEvaluation(this.evaluation),
                actions: this.actions,
            },
            expressionToParts: this.expressionToParts,
        };
    }
    convertEvaluation(evaluation) {
        const meaning = new EvaluationMeaning(evaluation).build();
        if (!meaning) {
            return [];
        }
        return meaning.rows.map((r) => ({
            id: r.id,
            createdVia: r.createdVia.id,
            elements: r.expressions.reduce((p, c) => {
                if (p.stop) {
                    return p;
                }
                if (c.status === Status.ok) {
                    this.expressionToParts.set(c.id, c.dependencies);
                    return {
                        ...p,
                        expressions: p.expressions.concat({
                            id: c.id,
                            createdAt: this.timestamp,
                            createdVia: this.getCreatedVia(c.createdVia),
                            content: {
                                expressions: [{ id: c.id, expression: c.grl }],
                            },
                        }),
                    };
                }
                else {
                    return { ...p, stop: true };
                }
            }, { expressions: [], stop: false }).expressions,
        }));
    }
    getCreatedVia(action) {
        if (!this.actions.includes(action)) {
            this.actions.push(action);
        }
        return action.id;
    }
}
function significantParts(parts) {
    return parts.filter((p) => p.expression !== 'error(placeholder)');
}
function startsWithPlaceholder(line) {
    if (line.parts.length === 0) {
        return false;
    }
    return line.parts[0].name === MathNodeName.placeholder;
}
function EvaluationLineIterator(evaluation) {
    let index = 0;
    return function nextLine() {
        return index < evaluation.lines.length ? evaluation.lines[index++] : null;
    };
}
function IdGenerator() {
    let id = 0;
    return function nextId() {
        return id--;
    };
}
class EvaluationMeaning {
    constructor(evaluation) {
        this.rows = [];
        this.currentRow = null;
        this.lastPart = null;
        this.curParts = null;
        this.nextParts = null;
        this.nextId = IdGenerator();
        this.evaluation = evaluation;
    }
    canBuildMeaning() {
        if (!this.evaluation.lines.length) {
            return false;
        }
        const nextLine = EvaluationLineIterator(this.evaluation);
        let cur = nextLine();
        let next = nextLine();
        return !(cur.parts.length < 3 && !next);
    }
    build() {
        if (!this.canBuildMeaning()) {
            return;
        }
        const nextLine = EvaluationLineIterator(this.evaluation);
        let cur = nextLine();
        let next = nextLine();
        this.createRow(null);
        while (next != null) {
            this.curParts = cur.mathElements;
            this.nextParts = next.mathElements;
            const filteredCurPartsCount = significantParts(this.curParts).length;
            if (!this.currentRow.meaningType) {
                if (this.nextParts.length < 2 || startsWithPlaceholder(next)) {
                    significantParts(this.curParts).forEach((p) => this.createEquivalentExpressionFromAnother(p));
                    this.currentRow.meaningType = MathEvaluationMeaningType.rewrite;
                }
                else {
                    if (filteredCurPartsCount > 2) {
                        significantParts(this.curParts)
                            .slice(0, filteredCurPartsCount - 1)
                            .forEach((p) => this.createEquivalentExpressionFromAnother(p));
                        this.currentRow.meaningType = MathEvaluationMeaningType.rewrite;
                        this.createRow(MathEvaluationMeaningType.transformation);
                        this.lastPart = this.curParts[this.curParts.length - 1];
                        this.createEquality(this.curParts[this.curParts.length - 2], this.lastPart);
                    }
                    if (filteredCurPartsCount === 2) {
                        this.lastPart = this.curParts[this.curParts.length - 1];
                        this.createEquality(this.curParts[this.curParts.length - 2], this.lastPart);
                        this.currentRow.meaningType = MathEvaluationMeaningType.transformation;
                    }
                    if (filteredCurPartsCount === 1) {
                        this.createEquivalentExpressionFromAnother(this.curParts[0]);
                        this.currentRow.meaningType = MathEvaluationMeaningType.rewrite;
                    }
                }
            }
            else {
                if (this.currentRow.meaningType === MathEvaluationMeaningType.rewrite) {
                    if (filteredCurPartsCount > 1) {
                        this.continueRewriteOrCreateEquality(next);
                    }
                    else {
                        significantParts(this.curParts).forEach((p) => this.createEquivalentExpressionFromAnother(p));
                    }
                }
                else {
                    if (this.currentRow.meaningType === MathEvaluationMeaningType.transformation) {
                        if (startsWithPlaceholder(cur)) {
                            this.createRow(MathEvaluationMeaningType.rewrite);
                            this.createEquivalentExpressionFromAnother(this.lastPart);
                            this.continueRewriteOrCreateEquality(next);
                        }
                        else {
                            if (this.curParts.length === 1) {
                                this.createRow(MathEvaluationMeaningType.rewrite);
                                this.createEquivalentExpressionFromAnother(this.lastPart);
                                this.createEquivalentExpressionFromAnother(this.curParts[0]);
                            }
                            this.lastPart = this.curParts[1];
                            this.createEquality(this.curParts[0], this.lastPart);
                            if (this.curParts.length > 2) {
                                this.createRow(MathEvaluationMeaningType.rewrite);
                                if (this.nextParts.length < 2 || startsWithPlaceholder(next)) {
                                    significantParts(this.curParts.slice(1)).forEach((p) => this.createEquivalentExpressionFromAnother(p));
                                }
                                else {
                                    significantParts(this.curParts.slice(1, -1)).forEach((p) => this.createEquivalentExpressionFromAnother(p));
                                    this.createRow(MathEvaluationMeaningType.transformation);
                                    this.lastPart = this.curParts[this.curParts.length - 1];
                                    this.createEquality(this.curParts[this.curParts.length - 2], this.lastPart);
                                }
                            }
                        }
                    }
                }
            }
            cur = next;
            next = nextLine();
        }
        this.curParts = cur.mathElements;
        if (this.currentRow.meaningType === MathEvaluationMeaningType.rewrite || this.currentRow.meaningType === null) {
            significantParts(this.curParts).forEach((p) => this.createEquivalentExpressionFromAnother(p));
        }
        if (this.currentRow.meaningType === MathEvaluationMeaningType.transformation) {
            if (this.curParts.length < 2 || startsWithPlaceholder(cur)) {
                this.createRow(MathEvaluationMeaningType.rewrite);
                this.createEquivalentExpressionFromAnother(this.lastPart);
                significantParts(this.curParts).forEach((p) => this.createEquivalentExpressionFromAnother(p));
            }
            else {
                this.createEquality(this.curParts[0], this.curParts[1]);
                if (this.curParts.length > 2) {
                    this.createRow(MathEvaluationMeaningType.rewrite);
                    this.curParts.slice(1).forEach((p) => this.createEquivalentExpressionFromAnother(p));
                }
            }
        }
        return {
            id: this.evaluation.id,
            rows: this.rows,
        };
    }
    continueRewriteOrCreateEquality(next) {
        if (this.nextParts.length < 2 || startsWithPlaceholder(next)) {
            significantParts(this.curParts).forEach((p) => this.createEquivalentExpressionFromAnother(p));
        }
        else {
            significantParts(this.curParts.slice(0, -1)).forEach((p) => this.createEquivalentExpressionFromAnother(p));
            this.createRow(MathEvaluationMeaningType.transformation);
            this.lastPart = this.curParts[this.curParts.length - 1];
            this.createEquality(this.curParts[this.curParts.length - 2], this.lastPart);
        }
    }
    createEquivalentExpressionFromAnother(expression) {
        this.createExpression(expression.expression, expression.status, [expression.id]);
    }
    createExpression(grl, status, dependencies) {
        const previousExpression = this.currentRow.expressions[this.currentRow.expressions.length - 1];
        this.currentRow.expressions.push({
            id: this.nextId(),
            grl,
            status,
            dependencies,
            createdVia: !this.currentRow.expressions.length
                ? this.currentRow.createdVia
                : {
                    id: this.nextId(),
                    actionId: REWRITE_ROW,
                    type: ActionType.local,
                    parameters: [
                        {
                            parameterId: ActionParameterKey.value,
                            parameterType: MathParameterType.selection,
                            selectionType: MathSelectionType.cell,
                            elements: [
                                {
                                    elementId: previousExpression.id,
                                    expression: previousExpression.grl,
                                    elementType: MathSourceActionParameterElementType.expression,
                                },
                            ],
                        },
                    ],
                },
        });
    }
    createEquality(left, right) {
        if (left && right) {
            const grl = left.expression + '=' + right.expression;
            const status = left.status === Status.error || right.status === Status.error ? Status.error : left.status;
            this.createExpression(grl, status, [left.id, right.id]);
        }
    }
    createRow(meaningType) {
        this.currentRow = {
            id: this.nextId(),
            meaningType,
            createdVia: {
                id: this.nextId(),
                actionId: ADD_ROW,
                type: ActionType.local,
                parameters: [],
            },
            expressions: [],
        };
        this.rows.push(this.currentRow);
    }
}
